"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleAlert as AlertCircle, Compass, Layers, ChartBar as BarChart3, BookOpen, ArrowUpRight, Download, CircleCheck as CheckCircle2, FileText, Clock, Building2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const phases = [{
  phase: "Phase 01",
  title: "The operational challenge",
  icon: AlertCircle,
  summary: "A fragmented B2B manufacturing conglomerate operated 14 disconnected regional CRM and ERP silos, obscuring market forecasting and introducing a 9-week lag in supply chain allocation decisions.",
  keyPoints: ["Inconsistent telemetry across four core operating divisions", "Manual reconciliations costing over 1,200 executive hours per quarter", "Vulnerability to procurement price swings due to delayed demand signals"],
  metric: "9-week",
  metricLabel: "Historical decision lag"
}, {
  phase: "Phase 02",
  title: "Strategic approach & methodology",
  icon: Compass,
  summary: "Rather than undertaking a risky single-cutover migration, the board enacted a phased harmonization protocol: establishing a unified schema layer without interrupting active enterprise resource planning systems.",
  keyPoints: ["Establishment of an independent data governance board across business units", "Selection of an open-standard API middleware framework", "Creation of an internal pilot unit to validate pipeline integrity"],
  metric: "100%",
  metricLabel: "Uptime maintained during rollout"
}, {
  phase: "Phase 03",
  title: "Execution framework",
  icon: Layers,
  summary: "A three-stage technical roll-out executed over 18 weeks. Data pipelines were standardized, validated against strict compliance benchmarks, and integrated into a real-time executive dashboard.",
  keyPoints: ["Sprint 1–6: Schema normalization and legacy API deprecation", "Sprint 7–12: Automated validation checks and role-based clearance", "Sprint 13–18: Cross-department onboarding and stress testing"],
  metric: "18 wks",
  metricLabel: "Total deployment timeline"
}, {
  phase: "Phase 04",
  title: "Measurable outcome",
  icon: BarChart3,
  summary: "The unified intelligence architecture reduced decision latency from 9 weeks to under 48 hours, enabling proactive supply procurement and eliminating duplicate analytical labor.",
  keyPoints: ["Decision velocity accelerated by 88% across operational units", "$4.2M captured in annualized procurement optimization savings", "Full compliance parity achieved ahead of audit deadlines"],
  metric: "88%",
  metricLabel: "Reduction in reporting latency"
}, {
  phase: "Phase 05",
  title: "Key executive learnings",
  icon: BookOpen,
  summary: "Institutional transformation succeeds when structural incentives align with data transparency. Technical modernization must be paired with clear operational ownership.",
  keyPoints: ["Treat enterprise data as an institutional asset rather than departmental property", "Prioritize schema consistency over front-end reporting complexity", "Empower cross-functional stewards to maintain validation standards continuously"],
  metric: "3x",
  metricLabel: "Faster quarterly strategic review cycle"
}];
const metadataItems = [{
  label: "Format",
  value: "Illustrative methodology study"
}, {
  label: "Target audience",
  value: "Chief operations & strategy officers"
}, {
  label: "Reading duration",
  value: "8 min detailed briefing"
}, {
  label: "Subject sector",
  value: "Industrial operations & supply chain"
}];
export default function CaseStudy() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="featured-case-study-detail" data-nav="light" className="relative bg-background py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12">
        {/* Masthead rule motif */}
        <div className="mb-10 space-y-1">
          <div className="h-[1px] w-full bg-border" />
          <div className="h-[0.5px] w-full bg-border/60" />
        </div>

        {/* Section Header */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-start">
          <div className="lg:col-span-8">
            <div className="flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center rounded-xs border border-border bg-card px-2.5 py-0.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                Sample Demonstration
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-0.5 text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
                <Building2 className="size-3.5" />
                Enterprise Operations
              </span>
            </div>

            <h1 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              Harmonizing decentralized enterprise data across 14 operating units
            </h1>

            <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              A structured walkthrough showing how an international industrial
              enterprise resolved fragmented operational telemetry, eliminated
              reporting bottlenecks, and accelerated quarterly strategic decision
              cycles.
            </p>
          </div>

          {/* Quick Specs Card */}
          <div className="lg:col-span-4">
            <Card className="border border-border shadow-xs">
              <CardContent className="space-y-4 p-5">
                <div className="flex items-center justify-between border-b border-border pb-3">
                  <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                    Briefing Overview
                  </span>
                  <FileText className="size-4 text-muted-foreground" />
                </div>
                <dl className="space-y-3">
                  {metadataItems.map((meta, i) => <div key={meta.label} data-index={i} className="flex flex-col text-sm sm:flex-row sm:justify-between sm:gap-2">
                      <dt className="text-xs text-muted-foreground font-medium">
                        {meta.label}
                      </dt>
                      <dd className="font-sans text-xs font-semibold text-foreground text-left sm:text-right">
                        {meta.value}
                      </dd>
                    </div>)}
                </dl>
                <Separator />
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="size-3.5" /> Published by Editorial Staff
                  </span>
                  <span className="font-mono">Peer-Reviewed</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Analytical Step-by-Step Walkthrough */}
        <div className="mt-16 lg:mt-20">
          <div className="mb-8 flex items-center justify-between border-b border-border pb-4">
            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground sm:text-2xl">
                Five-phase analytical breakdown
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Sequential examination from operational bottleneck to strategic resolution.
              </p>
            </div>
            <span className="hidden font-mono text-xs text-muted-foreground md:inline-block">
              Methodology Blueprint
            </span>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.1
        }} className="relative">
            {/* Connecting Vertical Line */}
            <div aria-hidden="true" className="absolute top-8 bottom-8 left-4.5 hidden w-[1px] bg-border md:block" />

            <div className="space-y-8 md:space-y-10">
              {phases.map((item, i) => {
              const Icon = item.icon;
              return <motion.div key={item.phase} data-index={i} variants={itemVariants} className="relative grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-8">
                    {/* Step Marker */}
                    <div className="md:col-span-1 flex md:flex-col items-center md:items-center">
                      <div className="relative z-10 flex size-9 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-2xs">
                        <Icon className="size-4 text-foreground" />
                      </div>
                    </div>

                    {/* Step Content */}
                    <div className="md:col-span-11">
                      <Card className="border border-border bg-card shadow-xs transition-[box-shadow,border-color] duration-200 hover:shadow-md hover:border-foreground/20">
                        <CardContent className="p-6 sm:p-8">
                          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-4">
                            <div className="flex items-center gap-3">
                              <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                                {item.phase}
                              </span>
                              <h3 className="font-serif text-xl font-semibold text-foreground sm:text-2xl">
                                {item.title}
                              </h3>
                            </div>
                            <div className="flex items-baseline gap-2 rounded-sm bg-muted/60 px-3 py-1.5 border border-border/60">
                              <span className="font-mono text-lg font-bold text-foreground">
                                {item.metric}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {item.metricLabel}
                              </span>
                            </div>
                          </div>

                          <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-12">
                            <div className="lg:col-span-7">
                              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                Synthesis & Context
                              </h4>
                              <p className="mt-2 text-sm leading-relaxed text-foreground/90 sm:text-base">
                                {item.summary}
                              </p>
                            </div>

                            <div className="lg:col-span-5 rounded-md bg-muted/40 p-4 border border-border/50">
                              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                Operational Actions
                              </h4>
                              <ul className="mt-2.5 space-y-2">
                                {item.keyPoints.map((point, pointIndex) => <li key={pointIndex} data-index={pointIndex} className="flex items-start gap-2 text-xs text-foreground/90 sm:text-sm">
                                    <CheckCircle2 className="size-4 shrink-0 text-foreground/70 mt-0.5" />
                                    <span>{point}</span>
                                  </li>)}
                              </ul>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </motion.div>;
            })}
            </div>
          </motion.div>
        </div>

        {/* Section Bottom Summary / Download Action */}
        <div className="mt-14 sm:mt-16 rounded-lg border border-border bg-card p-6 sm:p-8 lg:p-10 shadow-xs">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-8">
              <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                Editorial Resource
              </span>
              <h3 className="mt-1 font-serif text-2xl font-semibold text-foreground sm:text-3xl">
                Access the complete operational methodology library
              </h3>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
                Explore peer-reviewed research reports, strategic frameworks, and
                management blueprints curated for executive leadership.
              </p>
            </div>

            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 justify-start lg:justify-end">
              <Button asChild size="lg" className="w-full sm:w-auto font-medium">
                <Link to="/ResearchReportsPage">
                  <BookOpen className="size-4" />
                  <span>Browse research reports</span>
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium">
                <Link to="/ResourcesPage">
                  <span>View all resources</span>
                  <ArrowUpRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Bottom Rule Motif */}
        <div className="mt-14 sm:mt-16 space-y-1">
          <div className="h-[0.5px] w-full bg-border/60" />
          <div className="h-[1px] w-full bg-border" />
        </div>
      </div>
    </section>;
}